import { useEffect, useState, useCallback, useMemo } from "react";

import {
  Col,
  Form,
  Row,
  Card,
  Button,
  Container,
  Offcanvas,
} from "react-bootstrap";

import {
  CardComponent,
  InputEdit,
  AvatarRow,
  WarningCard,
  LoadingSpinner,
} from "components";
import { useAuth } from "utils/hooks";
import { useUsersApi, useGnssApi } from "utils/hooks";
import { useFilter } from "utils/hooks";
import { PlusSquare, Trash } from "react-bootstrap-icons";

const DeleteGroupModal = ({ groupDetails, onDelete }) => {
  const [isConfirmed, setConfirmed] = useState(false);

  const handleInputChange = (e) => {
    setConfirmed(e.target.value === groupDetails.groupName);
  };

  return (
    <>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group as={Col} className="mb-1">
          <span className="text-danger">
            You are about to remove <b>{groupDetails?.groupName}</b>. Please
            confirm by typing the group name below.
          </span>
        </Form.Group>
        <Form.Group as={Col} className="mb-4">
          <InputEdit
            label="Write group name for confirmation"
            size="sm"
            onChange={handleInputChange}
          />
        </Form.Group>
      </Form>
      <Row>
        <Col className="d-flex justify-content-end">
          <Button
            variant="success"
            size="sm"
            disabled={!isConfirmed}
            onClick={() => onDelete(groupDetails)}
          >
            Delete group
          </Button>
        </Col>
      </Row>
    </>
  );
};

const CreateGroupModal = ({ onCreate }) => {
  const [groupDetails, setGroupDetails] = useState({
    groupName: "",
    groupDescription: "",
  });

  const [isConfirmed, setConfirmed] = useState(false);

  const [errorMessages, setErrorMessage] = useState({
    name: null,
  });

  const handleNameChange = (e) => {
    setGroupDetails({ ...groupDetails, groupName: e.target.value });
  };

  const handleDescriptionChange = (e) => {
    setGroupDetails({ ...groupDetails, groupDescription: e.target.value });
  };

  return (
    <>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group as={Col} className="mb-4">
          <InputEdit
            label="Network name"
            size="sm"
            value={groupDetails.groupName}
            error={errorMessages.name}
            validationPattern={/[^a-zA-Z0-9 -]/g}
            onFocus={() => setErrorMessage({ ...errorMessages, name: null })}
            onBlur={(e) => {
              if (e.target.value.trim() === "") {
                setErrorMessage({
                  ...errorMessages,
                  name: "cannot be empty",
                });
                setConfirmed(false);
              } else {
                setConfirmed(true);
              }
            }}
            onChange={handleNameChange}
          />
        </Form.Group>
        <Form.Group as={Col} className="mb-4">
          <InputEdit
            label="Network description"
            size="sm"
            value={groupDetails.groupDescription}
            onChange={handleDescriptionChange}
          />
        </Form.Group>
      </Form>
      <Row>
        <Col className="d-flex justify-content-end">
          <Button
            variant="success"
            size="sm"
            disabled={!isConfirmed}
            onClick={() => onCreate(groupDetails)}
          >
            Create network
          </Button>
        </Col>
      </Row>
    </>
  );
};

export const NetworksManagement = () => {
  const avatarLimit = 8;
  const api = useUsersApi();
  const apiGnss = useGnssApi();
  const { verifyAuthorization, onRefreshToken } = useAuth();
  const { groupFilter, setGroupFilter } = useFilter();

  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [warning, setWarning] = useState({ title: "", message: "" });

  const handleClose = () => {
    setShowDeleteGroupModal(false);
    setShowCreateGroupModal(false);
  };

  const handleCreateGroup = (groupDetails) => {
    // TODO: add API call to create
    createGroup(groupDetails).then(() => {
      onRefreshToken().then(() => {
        getTenantGroups();
      });
    });

    setShowCreateGroupModal(false);
  };

  const handleDeleteGroup = (groupDetails) => {
    // TODO: add API call to delete
    deleteGroup(groupDetails.groupId).then(() => {
      onRefreshToken().then(() => {
        getTenantGroups();
      });
    });
    setShowDeleteGroupModal(false);
  };

  const getGroups = async (isTenantOwner) => {
    try {
      let response = await api.getGroupsDetails(avatarLimit);

      response = response.data;
      if (isTenantOwner)
        response = [
          // Hardcoded element
          ...response,
          { groupName: "UNGROUPED", groupId: "" },
        ];

      return response;
    } catch (e) {
      //console.error("Error fetching groups", e);
    }
  };

  const createGroup = async (groupDetails) => {
    const payload = {
      name: groupDetails.groupName,
      description: groupDetails.groupDescription,
    };

    try {
      const response = await api.createGroup(payload);
    } catch (e) {
      //console.error("Error creating group", e);
    }
  };

  const deleteGroup = async (groupId) => {
    await api.deleteGroup(groupId);
  };

  const getAllStationsDetails = async () => {
    try {
      const response = await apiGnss.getAllStationsDetails();

      return response.data;
    } catch (e) {
      //alert(e);
      //throw e;
    }
  };

  const getTenantGroups = async () => {
    setIsLoading(true);

    const responseGroups = await getGroups(
      verifyAuthorization(["Tenant Owner"]),
    );

    const responseStations = await getAllStationsDetails();
    setIsLoading(false);

    const result = responseGroups
      .filter((group) => {
        // Count stations for this group
        const hasStations = responseStations?.some((station) =>
          station.groupNames.includes(group.groupName),
        );

        // Skip "ungrouped" if it has no stations
        if (group.groupName === "UNGROUPED" && !hasStations) {
          return false;
        }

        return true;
      })
      .map((group) => {
        // Filter stations matching the groupName
        const filteredStations = responseStations?.filter((station) =>
          station.groupNames.includes(group.groupName),
        );

        // Get total count before slicing
        const totalMatchedStationsCount = filteredStations?.length;

        // Then slice and map
        const matchedStations = filteredStations
          ?.slice(0, avatarLimit)
          .map((station) => {
            const { identifier, name } = station;
            return { stationId: identifier, stationName: name };
          });
        return {
          ...group,
          stations: matchedStations,
          stationsCount: totalMatchedStationsCount,
        };
      });

    if (verifyAuthorization(["Tenant Owner"]) && result.length === 0) {
      setIsSuccess(false);
      setWarning({
        title: "No networks available",
        message: (
          <>
            Your company doesn't have any networks. In order to add a station
            you need to create a new network. <br></br>
            <Button
              className="m-1"
              variant="success"
              size="sm"
              onClick={() => setShowCreateGroupModal(true)}
            >
              Add new network
            </Button>
          </>
        ),
      });
      return;
    }

    if (verifyAuthorization(["Tenant User"]) && result.length === 0) {
      setIsSuccess(false);
      setWarning({
        title: "No networks available",
        message:
          "Your administrator hasn’t granted you access to any network. Please contact your administrator to request the required permissions.",
      });
      return;
    }
    setIsSuccess(true);
    setAvailableGroups(result);
  };

  useEffect(() => {
    getTenantGroups();
  }, []);

  const handleToggle = useCallback(
    (e, groupName) => {
      setGroupFilter((prev) =>
        e.target.checked
          ? prev.filter((name) => name !== groupName)
          : [...new Set([...prev, groupName])],
      );
    },
    [setGroupFilter],
  );

  const processedGroups = useMemo(() => {
    return availableGroups.map((group) => ({
      ...group,
      stations: group.stations || [],
      users: group.users || [],
    }));
  }, [availableGroups]);

  const toggleHandlers = useMemo(() => {
    const handlers = {};
    for (const group of availableGroups) {
      handlers[group.groupName] = (e) => handleToggle(e, group.groupName);
    }
    return handlers;
  }, [availableGroups, handleToggle]);

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      {!isSuccess && !isLoading ? (
        <WarningCard {...warning} />
      ) : (
        <Row className="m-3">
          {verifyAuthorization(["Tenant Owner"]) && (
            <Container fluid className="d-flex justify-content-end m-0 p-0">
              <Button
                className="m-1 d-flex align-items-center"
                variant="success"
                size="sm"
                onClick={() => setShowCreateGroupModal(true)}
              >
                <PlusSquare size={15} className="me-1" /> Create new network
              </Button>
            </Container>
          )}

          <div className="grid-groups">
            {processedGroups?.map((group) => (
              <CardComponent
                key={group.groupId}
                title={group.groupName}
                toggle={!groupFilter.includes(group.groupName)}
                onToggle={toggleHandlers[group.groupName]}
              >
                <Form
                  className="shadow  login-form mt-2 pt-0"
                  style={{ opacity: 0.9 }}
                >
                  <Card
                    key={group.groupId}
                    style={{ background: "transparent" }}
                  >
                    <Row>
                      <Col>
                        <Row className="p-0 m-0 g-0">
                          <Col
                            xs={4}
                            className="text-start fw-bold"
                            style={{ color: "#177ddc" }}
                          >
                            Stations:
                          </Col>
                          <Col
                            xs={8}
                            className="text-end"
                            style={{ color: "#dbdbdb" }}
                          >
                            {group.stationsCount || 0}
                          </Col>
                        </Row>

                        <Col
                          className="p-0 m-0 g-0 d-flex align-items-start"
                          style={{ position: "relative" }}
                        >
                          <AvatarRow items={group.stations} type="stations" />
                        </Col>

                        <Row className="p-0 m-0 g-0">
                          <Col
                            xs={4}
                            className="text-start fw-bold"
                            style={{ color: "#177ddc" }}
                          >
                            Users:
                          </Col>
                          <Col
                            xs={8}
                            className="text-end"
                            style={{ color: "#dbdbdb" }}
                          >
                            {group.users.length || 0}
                          </Col>
                        </Row>

                        <Col
                          className="p-0 m-0 g-0 d-flex align-items-start"
                          style={{ position: "relative" }}
                        >
                          <AvatarRow items={group.users} type="users" />
                        </Col>
                      </Col>
                    </Row>
                  </Card>
                </Form>
                {verifyAuthorization(["Tenant Owner"]) &&
                  group.groupName !== "UNGROUPED" && (
                    <Container
                      fluid
                      className="d-flex justify-content-end m-0 p-0"
                    >
                      <Button
                        variant="outline-danger"
                        title="Delete group"
                        className="m-1 d-flex align-items-center"
                        size="sm"
                        onClick={(e) => {
                          (setSelectedGroupId(group.groupId),
                            setShowDeleteGroupModal(true));
                        }}
                      >
                        <Trash size={15} className="me-1" />{" "}
                        <small>Delete</small>
                      </Button>
                    </Container>
                  )}
              </CardComponent>
            ))}
          </div>
        </Row>
      )}
      {verifyAuthorization(["Tenant Owner"]) && (
        <>
          <Offcanvas
            style={{
              borderLeft: "0.5pt solid #dbdbdb",
              backgroundColor: "#141414",
            }}
            show={showCreateGroupModal}
            onHide={handleClose}
            placement="end"
          >
            <Offcanvas.Header>
              <Offcanvas.Title style={{ color: "#dbdbdb" }}>
                Create new network
              </Offcanvas.Title>
              <button
                type="button"
                className="close"
                style={{
                  color: "#dbdbdb",
                  border: "none",
                  background: "none",
                  fontSize: "1.5rem",
                }}
                onClick={handleClose}
              >
                &times;
              </button>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <CreateGroupModal
                onClose={handleClose}
                onCreate={handleCreateGroup}
              />
            </Offcanvas.Body>
          </Offcanvas>

          <Offcanvas
            style={{
              borderLeft: "0.5pt solid #dbdbdb",
              backgroundColor: "#141414",
            }}
            show={showDeleteGroupModal}
            onHide={handleClose}
            placement="end"
          >
            <Offcanvas.Header>
              <Offcanvas.Title style={{ color: "#dbdbdb" }}>
                Delete network
              </Offcanvas.Title>
              <button
                type="button"
                className="close"
                style={{
                  color: "#dbdbdb",
                  border: "none",
                  background: "none",
                  fontSize: "1.5rem",
                }}
                onClick={handleClose}
              >
                &times;
              </button>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <DeleteGroupModal
                groupDetails={availableGroups.find(
                  (group) => group.groupId === selectedGroupId,
                )}
                onClose={handleClose}
                onDelete={handleDeleteGroup}
              />
            </Offcanvas.Body>
          </Offcanvas>
        </>
      )}
    </>
  );
};
